import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button } from '@artosera/ui';
import {
  lireProfilSources,
  type ColonneProfilee,
  type FichierProfile,
  type SuggestionProfil,
} from '../../../../lib/correspondance.js';
import { colonnesAmbigues } from '../../../../lib/reglesEdition.js';
import { formaterOctets } from '../../../../lib/format.js';
import type { LotReprise } from '../../../../lib/reprise.js';
import pageStyles from '../../../../styles/page.module.css';
import tableStyles from '../../../../styles/table.module.css';
import { messageReprise } from '../messages.js';
import reprise from '../Reprise.module.css';
import styles from './Correspondance.module.css';

export const cleProfilSources = (idGalerie: string, lot: LotReprise): readonly string[] => [
  'console',
  'galerie',
  idGalerie,
  'reprise',
  'lot',
  lot.id,
  'profil-sources',
  lot.travail?.id ?? '',
];

function Frequentes({ colonne }: { colonne: ColonneProfilee }): JSX.Element {
  const { t } = useTranslation('console');
  if (colonne.frequentes.length === 0)
    return <span className={reprise.discret}>{t('correspondance.profil.aucuneValeur')}</span>;
  const [debut, reste] = [colonne.frequentes.slice(0, 3), colonne.frequentes.slice(3)];
  const ligne = (valeur: string, nombre: number): string => `${valeur} (${String(nombre)})`;
  return (
    <div className={styles.frequentes}>
      <span>{debut.map((v) => ligne(v.valeur, v.nombre)).join(' · ')}</span>
      {reste.length > 0 ? (
        <details>
          <summary>{t('correspondance.profil.autresValeurs', { count: reste.length })}</summary>
          <ul className={styles.listeSimple}>
            {reste.map((v) => (
              <li key={v.valeur}>{ligne(v.valeur, v.nombre)}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function TableColonnes({ fichier }: { fichier: FichierProfile }): JSX.Element {
  const { t } = useTranslation('console');
  return (
    <>
      {(fichier.tables ?? []).map((table) => (
        <div key={table.nom ?? 'unique'} className={reprise.pile}>
          <h4 className={reprise.sousTitre}>
            {table.nom
              ? t('correspondance.profil.tableNommee', { nom: table.nom, count: table.lignes })
              : t('correspondance.profil.tableUnique', { count: table.lignes })}
          </h4>
          <table className={`${tableStyles.table} ${reprise.table} ${styles.tableProfil}`}>
            <thead>
              <tr>
                <th scope="col">{t('correspondance.profil.colonne')}</th>
                <th scope="col">{t('correspondance.profil.remplissage')}</th>
                <th scope="col">{t('correspondance.profil.frequentes')}</th>
                <th scope="col">{t('correspondance.profil.format')}</th>
              </tr>
            </thead>
            <tbody>
              {table.colonnes.map((colonne) => (
                <tr
                  key={colonne.nom}
                  data-ambigue={colonne.format === 'date_ambigue' ? 'true' : undefined}
                >
                  <td data-label={t('correspondance.profil.colonne')}>
                    <span className={styles.nomColonne}>{colonne.nom}</span>
                  </td>
                  <td data-label={t('correspondance.profil.remplissage')}>
                    <meter
                      className={styles.jauge}
                      min={0}
                      max={1}
                      value={colonne.remplissage}
                      aria-label={t('correspondance.profil.remplissageColonne', {
                        nom: colonne.nom,
                      })}
                    />{' '}
                    <span className={styles.taux}>{Math.round(colonne.remplissage * 100)} %</span>
                  </td>
                  <td data-label={t('correspondance.profil.frequentes')}>
                    <Frequentes colonne={colonne} />
                  </td>
                  <td data-label={t('correspondance.profil.format')}>
                    {colonne.format === 'date_ambigue' ? (
                      <div className={styles.formatAmbigu}>
                        <Badge tone="reserved">
                          {t('correspondance.profil.formats.date_ambigue')}
                        </Badge>
                        <span>{t('correspondance.profil.ambigueAide')}</span>
                      </div>
                    ) : (
                      t(`correspondance.profil.formats.${colonne.format}`)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}

/**
 * Le profil des sources d'un lot (`profil.json`) : pour chaque fichier son
 * format et son encodage, pour chaque colonne son remplissage, ses valeurs
 * les plus fréquentes et le format détecté. Les dates ambiguës et les
 * fichiers illisibles passent devant : ce sont eux qui demandent une décision.
 */
export function ProfilSources({
  idGalerie,
  lot,
  peutAgir,
  appliquant,
  onAppliquer,
}: {
  idGalerie: string;
  lot: LotReprise;
  peutAgir: boolean;
  appliquant: boolean;
  onAppliquer: (suggestion: SuggestionProfil) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const profil = useQuery({
    queryKey: cleProfilSources(idGalerie, lot),
    queryFn: () => lireProfilSources(idGalerie, lot.id),
    enabled: lot.profilage?.fait === true,
  });

  if (lot.profilage?.fait !== true) return <></>;

  const ambigues = profil.data ? colonnesAmbigues(profil.data) : [];
  const illisibles = (profil.data?.fichiers ?? []).filter((fichier) => fichier.erreur);
  const suggestions = new Map<string, SuggestionProfil>();
  for (const fichier of profil.data?.fichiers ?? [])
    for (const suggestion of fichier.suggestions ?? [])
      if (suggestion.nature === lot.nature)
        suggestions.set(`${suggestion.profilId}@${String(suggestion.version)}`, suggestion);
  const classees = [...suggestions.values()].sort((a, b) => b.score - a.score);

  return (
    <section className={pageStyles.panneau} aria-labelledby="corr-profil">
      <div className={pageStyles.sectionTitre}>
        <h2 id="corr-profil">{t('correspondance.profil.titre')}</h2>
      </div>
      <div className={reprise.pile}>
        <p className={reprise.texte}>{t('correspondance.profil.texte')}</p>
        {profil.isLoading ? <p className={reprise.discret}>{t('reprise.chargement')}</p> : null}
        {profil.isError ? (
          <p className={reprise.erreur} role="alert">
            {messageReprise(t, profil.error) ?? t('correspondance.profil.erreur')}
          </p>
        ) : null}

        {ambigues.length > 0 ? (
          <div className={`${reprise.avertissement} ${styles.note}`} role="note">
            <strong>{t('correspondance.profil.ambiguesTitre', { count: ambigues.length })}</strong>
            <ul className={styles.listeSimple}>
              {ambigues.map((a) => (
                <li key={`${a.fichier}-${a.colonne}`}>
                  {t('correspondance.profil.ambigueLigne', {
                    colonne: a.colonne,
                    fichier: a.fichier,
                    exemple: a.exemple,
                  })}
                </li>
              ))}
            </ul>
            <span>{t('correspondance.profil.ambiguesRegle')}</span>
          </div>
        ) : null}
        {illisibles.length > 0 ? (
          <div className={reprise.erreur} role="alert">
            <strong>
              {t('correspondance.profil.illisiblesTitre', { count: illisibles.length })}
            </strong>
          </div>
        ) : null}

        {(profil.data?.fichiers ?? []).map((fichier) => (
          <article
            key={fichier.fichierId ?? fichier.chemin}
            className={reprise.carte}
            aria-label={fichier.chemin}
          >
            <div className={reprise.rangee}>
              <h3 className={`${reprise.sousTitre} ${reprise.chemin}`}>{fichier.chemin}</h3>
              {fichier.format ? <Badge tone="neutral">{fichier.format}</Badge> : null}
            </div>
            <p className={reprise.discret}>
              {[
                formaterOctets(fichier.octets),
                fichier.encodage
                  ? t('correspondance.profil.encodage', { valeur: fichier.encodage })
                  : null,
                fichier.separateur
                  ? t('correspondance.profil.separateur', { valeur: fichier.separateur })
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            {fichier.erreur ? (
              <p className={reprise.erreur} role="alert">
                {t('correspondance.profil.illisible', { message: fichier.erreur.message })}
              </p>
            ) : (
              <TableColonnes fichier={fichier} />
            )}
          </article>
        ))}

        {profil.isSuccess ? (
          <div className={reprise.pile}>
            <h3 className={reprise.sousTitre}>{t('correspondance.suggestions.titre')}</h3>
            {classees.length === 0 ? (
              <p className={reprise.discret}>{t('correspondance.suggestions.aucune')}</p>
            ) : (
              <ul className={reprise.cartes}>
                {classees.map((suggestion) => {
                  const utilise =
                    lot.profil?.id === suggestion.profilId &&
                    lot.profil.version === suggestion.version;
                  return (
                    <li
                      key={`${suggestion.profilId}@${String(suggestion.version)}`}
                      className={reprise.carte}
                    >
                      <div className={reprise.rangee}>
                        <h4 className={reprise.sousTitre}>{suggestion.nom}</h4>
                        <Badge tone={suggestion.score >= 70 ? 'available' : 'neutral'}>
                          {t('correspondance.suggestions.score', { score: suggestion.score })}
                        </Badge>
                        {utilise ? (
                          <Badge tone="reserved">{t('correspondance.suggestions.utilise')}</Badge>
                        ) : null}
                      </div>
                      <p className={reprise.discret}>
                        {t('correspondance.suggestions.detail', {
                          version: suggestion.version,
                          trouves: suggestion.trouves,
                          attendus: suggestion.attendus,
                        })}
                      </p>
                      {peutAgir && !utilise ? (
                        <div className={reprise.actions}>
                          <Button
                            variant="secondary"
                            loading={appliquant}
                            onClick={() => onAppliquer(suggestion)}
                          >
                            {t('correspondance.suggestions.appliquer')}
                          </Button>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
            {classees.length > 0 && lot.transformation?.fait ? (
              <p className={reprise.discret}>{t('correspondance.suggestions.defait')}</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
